/**
 * PublicBreadcrumbs — breadcrumb trail for the /p/* public pages.
 *
 * Renders the trail as one capsule — a warm sand pill holding a home icon, then one
 * crumb per URL segment separated by chevrons, with the current page raised in
 * white. The table crumb is pluralised ("Event" → "Events"): it is the list.
 *
 * Labels are humanised rather than echoing raw URL segments — "loan_applications"
 * reads as "Loan Applications", the "/p" routing prefix is left out, and
 * an id segment becomes the record's own title when the page supplies one via
 * useCrumbLabel (falling back to "#42" / a shortened uuid).
 *
 * Long trails collapse to  Home › … › parent › current , with the ellipsis
 * expanding the hidden crumbs on click. On narrow screens the row scrolls
 * horizontally instead of wrapping, so the current page stays on one line.
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
// import { useTheme } from '../../theme/ThemeContext';
import { usePublicApp } from '../../context/PublicAppContext';

// The "/p" routing prefix is plumbing rather than a place the user recognises,
// and not a page at all (it answers 404), so it gets no crumb to click through
// to. Only as the first segment — a table could itself be called "p".
const HIDDEN_PREFIX = 'p';

/**
 * Routes that are not a place inside the site, and so have no trail.
 *
 * `/checkout/return` is where the payment gateway drops somebody. Its segments
 * would read "Home › Checkout › Return", and the "Checkout" crumb would link to
 * a route this app does not have — a trail made of one 404 and one word for a
 * screen whose only job is to say whether the money arrived.
 *
 * `/results/<race id>` is the motion board, and has the same shape of problem:
 * it would read "Home › Results › #16", where "Results" links to a route this
 * app does not have and "#16" is a race id nobody recognises. There is no list
 * of every race's results to climb up to — /results now lists the boards, but a
 * board is still reached from its event or
 * from a shared link.
 */
const TRAILLESS_PREFIXES = ['/checkout', '/results'];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Trails longer than this collapse their middle crumbs behind an ellipsis.
const MAX_VISIBLE = 4;

function isIdSegment(segment) {
  return /^\d+$/.test(segment) || UUID_RE.test(segment);
}

/** "loan_applications" → "Loan Applications"; ids → "#42" / "#a1b2c3d4…". */
function humanise(segment) {
  if (isIdSegment(segment)) {
    return UUID_RE.test(segment) ? `#${segment.slice(0, 8)}…` : `#${segment}`;
  }

  return segment
    .replace(/[-_]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * "Event" → "Events": the table crumb names the LIST it links to, so it reads
 * as a plural. Only the regular English endings; anything already ending in
 * "s" is left alone.
 */
function pluralise(label) {
  if (/s$/i.test(label)) return label;
  if (/[^aeiou]y$/i.test(label)) return `${label.slice(0, -1)}ies`;
  return `${label}s`;
}

export default function PublicBreadcrumbs() {
  // Capsule colours are fixed (as in PublicHeader), so the theme accent is no
  // longer read here. Previously: const { theme } = useTheme();
  const { crumbLabel } = usePublicApp();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);

  const segments = location.pathname.split('/').filter(Boolean);

  // Collapse state belongs to a trail, not to the component — a new route gets
  // a freshly collapsed trail rather than inheriting the last page's expansion.
  useEffect(() => { setExpanded(false); }, [location.pathname]);

  // Nothing useful to show when we are already at the site root.
  if (segments.length === 0) return null;
  if (TRAILLESS_PREFIXES.some(
    (prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`),
  )) return null;

  // const accent = theme.primary;

  const crumbs = [
    { label: 'Home', url: '/', icon: 'bi-house-door', iconOnly: true },
    ...segments.flatMap((segment, idx) => {
      if (idx === 0 && segment === HIDDEN_PREFIX) return [];
      const decoded = decodeURIComponent(segment);
      // The segment after "/p" is the table: its crumb is the list page.
      const isTable = idx === 1 && segments[0] === HIDDEN_PREFIX && !isIdSegment(decoded);
      return [{
        label: isTable ? pluralise(humanise(decoded)) : humanise(decoded),
        // The raw segment is worth keeping as a tooltip when the label hides it
        // (a shortened uuid) or when the chip itself is ellipsised.
        title: decoded,
        url: '/' + segments.slice(0, idx + 1).join('/'),
      }];
    }),
  ];

  // Only the hidden prefix ("/p" itself): a lone Home crumb is no trail.
  if (crumbs.length === 1) return null;

  // A page that knows its own name (a record title, a view's page_title) wins
  // over the URL segment — but only for the exact path it was recorded against.
  const last = crumbs[crumbs.length - 1];
  if (crumbLabel?.label && crumbLabel.path === location.pathname) {
    last.label = crumbLabel.label;
    last.title = crumbLabel.label;
  }

  // Home › … › parent › current. Keeping the parent visible means the collapsed
  // trail still answers "where am I and what is one level up?".
  const collapsed = !expanded && crumbs.length > MAX_VISIBLE;
  const visible = collapsed
    ? [crumbs[0], { ellipsis: true, hidden: crumbs.length - 3 }, ...crumbs.slice(-2)]
    : crumbs;

  // Capsule breadcrumb ("C2 · Warm sand"): one neutral sand pill; the
  // ancestors are soft ink links and the current page sits raised in white.
  const css = `
    .pb-nav { position: relative; z-index: 1; padding-top: 12px; padding-bottom: 0; } /* was padding-top 10px */
    .pb-list {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      max-width: 100%;
      box-sizing: border-box;
      list-style: none;
      margin: 0;
      padding: 3px; /* was 4px */
      border-radius: 999px;
      background: #F4EFEA; /* was peach #FFF1E8 */
      overflow-x: auto;
      scrollbar-width: none;
      -ms-overflow-style: none;
    }
    .pb-list::-webkit-scrollbar { display: none; }
    .pb-item { display: flex; align-items: center; flex: 0 0 auto; min-width: 0; }

    .pb-crumb {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      max-width: 26ch;
      height: 28px; /* was 34px */
      box-sizing: border-box;
      padding: 0 11px; /* was 0 12px */
      border: 0;
      border-radius: 999px;
      font-family: "Hanken Grotesk", "Inter", system-ui, sans-serif;
      font-size: 13.5px; /* was 14px */
      font-weight: 600;
      line-height: 1;
      color: rgba(36, 23, 8, 0.64); /* was orange #C2410C */
      text-decoration: none;
      white-space: nowrap;
      overflow: hidden;
      background: transparent;
      transition: background 160ms ease, color 160ms ease;
    }
    a.pb-crumb:hover { color: #241708; background: rgba(36, 23, 8, 0.06); } /* was #9A3412 on rgba(255,106,26,.12) */
    .pb-crumb:focus-visible { outline: 2px solid #FF6A1A; outline-offset: 1px; }

    .pb-text { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

    /* Where you are: raised in white, in ink rather than orange. */
    .pb-current {
      padding: 0 13px; /* was 0 14px */
      color: #241708;
      font-weight: 700;
      background: #FFFFFF;
      box-shadow: 0 1px 2px rgba(36, 23, 8, 0.06), 0 6px 14px -8px rgba(36, 23, 8, 0.25);
      cursor: default;
      max-width: 44ch;
    }

    .pb-sep {
      flex: 0 0 auto;
      margin: 0 1px;
      font-size: 0.72rem;
      color: rgba(36, 23, 8, 0.28); /* was rgba(194,65,12,.45) */
      user-select: none;
      -webkit-text-stroke: 0.3px currentColor;
    }

    .pb-ellipsis { cursor: pointer; font-weight: 700; letter-spacing: 0.05em; padding: 0 10px; }
    .pb-ellipsis:hover { color: #241708; background: rgba(36, 23, 8, 0.06); } /* was rgba(255,106,26,.12) */

    .pb-home { width: 28px; padding: 0; justify-content: center; } /* was 34px */
    .pb-home i { font-size: 0.88rem; } /* was 0.95rem */
    .pb-sr {
      position: absolute; width: 1px; height: 1px;
      padding: 0; margin: -1px; overflow: hidden;
      clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
    }

    @media (max-width: 576px) {
      .pb-crumb { max-width: 14ch; font-size: 13px; padding: 0 9px; } /* was 0 10px */
      .pb-current { max-width: 22ch; }
    }
  `;

  // Previous chip styling (accent-tinted chips per ancestor, plain current
  // page), kept for reference:
  // const css = `
  //   .pb-nav { position: relative; z-index: 1; }
  //   .pb-list {
  //     display: flex;
  //     align-items: center;
  //     gap: 1px;
  //     list-style: none;
  //     margin: 0;
  //     padding: 0;
  //     overflow-x: auto;
  //     scrollbar-width: none;
  //     -ms-overflow-style: none;
  //   }
  //   .pb-list::-webkit-scrollbar { display: none; }
  //   .pb-item { display: flex; align-items: center; flex: 0 0 auto; }
  // 
  //   .pb-crumb {
  //     display: inline-flex;
  //     align-items: center;
  //     gap: 6px;
  //     max-width: 26ch;
  //     padding: 5px 10px;
  //     border: 1px solid transparent;
  //     border-radius: 8px;
  //     font-size: 0.82rem;
  //     font-weight: 500;
  //     line-height: 1.3;
  //     color: #64748b;
  //     text-decoration: none;
  //     white-space: nowrap;
  //     overflow: hidden;
  //     background: transparent;
  //     transition: color 160ms ease, background 160ms ease, border-color 160ms ease;
  //   }
  //   /* The navigable ancestors carry the emphasis — they are the actionable part
  //      of the trail, so they read as filled accent chips. */
  //   a.pb-crumb {
  //     color: ${accent};
  //     font-weight: 600;
  //     background: ${accent}14;
  //     border-color: ${accent}2b;
  //   }
  //   a.pb-crumb:hover {
  //     color: ${accent};
  //     background: ${accent}26;
  //     border-color: ${accent}59;
  //   }
  //   .pb-crumb:focus-visible {
  //     outline: none;
  //     box-shadow: 0 0 0 3px ${accent}33;
  //     border-color: ${accent}66;
  //   }
  // 
  //   .pb-text {
  //     min-width: 0;
  //     overflow: hidden;
  //     text-overflow: ellipsis;
  //     white-space: nowrap;
  //   }
  // 
  //   /* Current page — plain and unchipped. It is where you already are, so it
  //      stays quiet and lets the links you can act on stand out. */
  //   .pb-current {
  //     color: #64748b;
  //     font-weight: 500;
  //     background: transparent;
  //     border-color: transparent;
  //     cursor: default;
  //     max-width: 44ch;
  //   }
  // 
  //   /* The chevron carries the "trail" reading of the row, so it is sized and
  //      inked to stay legible rather than fading into the background. */
  //   .pb-sep {
  //     flex: 0 0 auto;
  //     font-size: 0.78rem;
  //     font-weight: 700;
  //     color: #475569;
  //     opacity: 0.9;
  //     margin: 0 3px;
  //     user-select: none;
  //     -webkit-text-stroke: 0.4px currentColor;
  //   }
  // 
  //   /* Also actionable, so it carries the same chip emphasis as the links. */
  //   .pb-ellipsis {
  //     cursor: pointer;
  //     color: ${accent};
  //     background: ${accent}14;
  //     border-color: ${accent}2b;
  //     font-weight: 700;
  //     letter-spacing: 0.05em;
  //     padding: 5px 8px;
  //   }
  //   .pb-ellipsis:hover { color: ${accent}; background: ${accent}26; border-color: ${accent}59; }
  // 
  //   .pb-home i { font-size: 0.9rem; }
  //   .pb-sr {
  //     position: absolute; width: 1px; height: 1px;
  //     padding: 0; margin: -1px; overflow: hidden;
  //     clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
  //   }
  // 
  //   @media (max-width: 576px) {
  //     .pb-crumb { max-width: 14ch; font-size: 0.78rem; padding: 5px 8px; }
  //     .pb-current { max-width: 20ch; }
  //   }
  // `;


  return (
    // Previously: className="pb-nav container pt-3 pb-1" (16px above, 4px below).
    // The capsule is 10px taller than the old chips, so its padding is set in
    // .pb-nav instead to keep the same 52px footprint above the page content.
    <nav aria-label="Breadcrumb" className="pb-nav container">
      <style>{css}</style>
      <ol className="pb-list">
        {visible.map((crumb, idx) => {
          const isLast = idx === visible.length - 1;
          const sep = !isLast && (
            <i className="bi bi-chevron-right pb-sep" aria-hidden="true" />
          );

          if (crumb.ellipsis) {
            return (
              <li className="pb-item" key="__ellipsis">
                <button
                  type="button"
                  className="pb-crumb pb-ellipsis"
                  onClick={() => setExpanded(true)}
                  aria-label={`Show ${crumb.hidden} hidden breadcrumb ${crumb.hidden === 1 ? 'level' : 'levels'}`}
                  title="Show full path"
                >
                  …
                </button>
                {sep}
              </li>
            );
          }

          return (
            <li className="pb-item" key={crumb.url}>
              {isLast ? (
                <span
                  className="pb-crumb pb-current"
                  aria-current="page"
                  title={crumb.title}
                >
                  {crumb.icon && <i className={`bi ${crumb.icon}`} aria-hidden="true" />}
                  {crumb.iconOnly
                    ? <span className="pb-sr">{crumb.label}</span>
                    : <span className="pb-text">{crumb.label}</span>}
                </span>
              ) : (
                <Link
                  to={crumb.url}
                  className={`pb-crumb${crumb.iconOnly ? ' pb-home' : ''}`}
                  title={crumb.title || crumb.label}
                >
                  {crumb.icon && <i className={`bi ${crumb.icon}`} aria-hidden="true" />}
                  {crumb.iconOnly
                    ? <span className="pb-sr">{crumb.label}</span>
                    : <span className="pb-text">{crumb.label}</span>}
                </Link>
              )}
              {sep}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
